/**
 * Chrominance-based pulse extraction.
 *
 * Green-channel-only rPPG has a fundamental weakness: any change in
 * illumination or head position moves the green channel too, and the estimator
 * cannot tell that from a heartbeat. Chrominance methods exploit the fact that
 * a *pulse* and a *motion artefact* move the RGB channels along different
 * directions in colour space, and project the signal onto the plane where the
 * pulse lives and the artefact largely does not.
 *
 * POS (plane-orthogonal-to-skin) is the method implemented here. It matters
 * disproportionately for this product: on darker skin the pulsatile component
 * is already 5–20% of what it is on the lightest skin, so an artefact that
 * would be a minor annoyance at Monk 1 completely buries the signal at Monk 9.
 * Rejecting artefacts is worth more to the people this exists for than to
 * anyone else.
 *
 * Reference: Wang et al., "Algorithmic Principles of Remote PPG",
 * IEEE Transactions on Biomedical Engineering, 2017.
 */

/** Temporal mean of a channel, used to normalise away the DC skin tone. */
function mean(x) {
  let s = 0;
  for (let i = 0; i < x.length; i++) s += x[i];
  return s / x.length;
}

function stdDev(x) {
  const m = mean(x);
  let v = 0;
  for (let i = 0; i < x.length; i++) v += (x[i] - m) ** 2;
  return Math.sqrt(v / x.length);
}

/**
 * POS over a whole window.
 *
 * Normalise each channel by its own temporal mean (removing the skin tone,
 * which is exactly the variable that otherwise dominates), project onto two
 * directions chosen to be orthogonal to the skin-tone axis, then combine them
 * with a weight that cancels the residual intensity variation.
 *
 * @param {{r: ArrayLike<number>, g: ArrayLike<number>, b: ArrayLike<number>}} rgb
 * @returns {Float64Array} the extracted pulse signal, zero-mean
 */
export function pos(rgb) {
  const { r, g, b } = rgb;
  const n = Math.min(r.length, g.length, b.length);
  if (n < 8) return new Float64Array(0);

  const mr = mean(r) || 1;
  const mg = mean(g) || 1;
  const mb = mean(b) || 1;

  // Projection onto the plane orthogonal to the skin-tone direction.
  const s1 = new Float64Array(n);
  const s2 = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const rn = r[i] / mr;
    const gn = g[i] / mg;
    const bn = b[i] / mb;
    s1[i] = gn - bn;
    s2[i] = gn + bn - 2 * rn;
  }

  // Alpha-tuning: scale s2 so the intensity component present in both cancels.
  const sd2 = stdDev(s2);
  const alpha = sd2 === 0 ? 0 : stdDev(s1) / sd2;

  const out = new Float64Array(n);
  for (let i = 0; i < n; i++) out[i] = s1[i] + alpha * s2[i];

  const m = mean(out);
  for (let i = 0; i < n; i++) out[i] -= m;
  return out;
}

/**
 * CHROM, kept for comparison and as a fallback.
 *
 * Simpler and slightly cheaper than POS, and on a still subject in good light
 * the two are close. POS pulls ahead exactly where it matters — motion, and
 * uneven illumination.
 */
export function chrom(rgb) {
  const { r, g, b } = rgb;
  const n = Math.min(r.length, g.length, b.length);
  if (n < 8) return new Float64Array(0);

  const mr = mean(r) || 1;
  const mg = mean(g) || 1;
  const mb = mean(b) || 1;

  const x = new Float64Array(n);
  const y = new Float64Array(n);
  for (let i = 0; i < n; i++) {
    const rn = r[i] / mr;
    const gn = g[i] / mg;
    const bn = b[i] / mb;
    x[i] = 3 * rn - 2 * gn;
    y[i] = 1.5 * rn + gn - 1.5 * bn;
  }

  const sy = stdDev(y);
  const alpha = sy === 0 ? 0 : stdDev(x) / sy;

  const out = new Float64Array(n);
  for (let i = 0; i < n; i++) out[i] = x[i] - alpha * y[i];
  const m = mean(out);
  for (let i = 0; i < n; i++) out[i] -= m;
  return out;
}

/** Green channel alone. The baseline every other method has to beat. */
export function greenOnly(rgb) {
  const g = rgb.g;
  const out = new Float64Array(g.length);
  const m = mean(g);
  for (let i = 0; i < g.length; i++) out[i] = g[i] - m;
  return out;
}

export const EXTRACTORS = Object.freeze({ pos, chrom, green: greenOnly });

/**
 * Perfusion index computed the honest way for a chrominance signal: the
 * pulsatile amplitude relative to the DC level of the channel that carries it.
 */
export function chrominancePerfusion(rgb, pulse) {
  const dc = mean(rgb.g) || 1;
  if (!pulse.length) return 0;
  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < pulse.length; i++) {
    if (pulse[i] < min) min = pulse[i];
    if (pulse[i] > max) max = pulse[i];
  }
  return (max - min) / dc;
}
